import BaseEntityService from '../core/base-entity-service';

// Using 'object' for TradingView layout content as it's an opaque structure
// that gets serialized/deserialized without inspection
export interface UpsertSymbolLayoutDto {
  symbol: string;
  theme: string;
  content: object;
}

export interface SymbolLayout {
  id: string;
  symbol: string;
  type: string;
  theme: string;
  content: object;
}

// Named template types
export interface CreateTemplateDto {
  name: string;
  theme: 'light' | 'dark';
  content: object;
}

export interface UpdateTemplateDto {
  name?: string;
  content?: object;
}

export interface TemplateLayout {
  id: string;
  name: string;
  type: 'NAMED_TEMPLATE';
  theme: string;
  content: object;
  updatedAt: string;
}

export default class ChartLayoutService extends BaseEntityService {
  static override route = 'charts';

  async getSymbolLayout(symbol: string): Promise<SymbolLayout | null> {
    const url = `${this.path}/layouts/symbol/${symbol}`;

    try {
      return await this.httpService.get<SymbolLayout>(url);
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 404
      ) {
        return null;
      }
      throw error;
    }
  }

  async upsertSymbolLayout(dto: UpsertSymbolLayoutDto): Promise<SymbolLayout> {
    const url = `${this.path}/layouts/symbol`;
    return this.httpService.put<UpsertSymbolLayoutDto, SymbolLayout>(url, dto);
  }

  async deleteSymbolLayout(symbol: string): Promise<void> {
    const url = `${this.path}/layouts/symbol/${symbol}`;
    await this.httpService.delete<void>(url);
  }

  // Named template methods
  async getTemplates(): Promise<TemplateLayout[]> {
    // NOTE: Removed ?type=NAMED_TEMPLATE as backend doesn't support this query param
    // The /layouts endpoint returns all layouts for the user; symbol layouts use separate endpoint
    const url = `${this.path}/layouts`;

    const result = await this.httpService.get<{ data: TemplateLayout[] }>(url);
    // Backend returns { data: [...], total: number }
    return Array.isArray(result) ? result : result?.data || [];
  }

  async getTemplate(id: string): Promise<TemplateLayout | null> {
    const url = `${this.path}/layouts/${id}`;

    try {
      return await this.httpService.get<TemplateLayout>(url);
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 404
      ) {
        return null;
      }
      throw error;
    }
  }

  async createTemplate(dto: CreateTemplateDto): Promise<TemplateLayout> {
    const url = `${this.path}/layouts`;
    const payload = { ...dto, type: 'named_template' as const };

    return this.httpService.post<
      CreateTemplateDto & { type: 'named_template' },
      TemplateLayout
    >(url, payload);
  }

  async updateTemplate(
    id: string,
    dto: UpdateTemplateDto,
  ): Promise<TemplateLayout> {
    const url = `${this.path}/layouts/${id}`;

    return this.httpService.patch<UpdateTemplateDto, TemplateLayout>(url, dto);
  }

  async deleteTemplate(id: string): Promise<void> {
    const url = `${this.path}/layouts/${id}`;
    await this.httpService.delete<void>(url);
  }
}
